









































































































































import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { dataURItoBlob } from "@/lib/files/imageResizer";
import { requiredRule } from "@/lib/rules/requiredRule";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { downloadUrlFromRemoteOrigin } from "@/lib/utility/downloadFileFunc";
import { handleError } from "@/lib/utility/handleError";
import { createFileName, FileType } from "@/lib/utility/string-helper";
import { $t } from "@/lib/utility/t";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { IPopulatedSnapshotSignRequest } from "@/models/populated-snapshot-sign-request.entity";
import { SnapshotSignRequest } from "@/models/snapshot-sign-request.entity";
import snapshotSignResponseService from "@/services/mrfiktiv/services/snapshot-sign-response.service";
import { MrfiktivCreateSignDtoGen, MrfiktivDocumentViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { mixins } from "vue-class-component";
import { Component, Prop, Ref } from "vue-property-decorator";
import { VueSignaturePad } from "vue-signature-pad";
import MHeader from "../utility/mmmint/MHeader.vue";
import SnapshotDetail from "./SnapshotDetail.vue";

@Component({
  components: { VueSignaturePad, SnapshotDetail, MHeader }
})
export default class SnapshotSignResponseCard extends mixins(RulesMixin) {
  readonly RequestStatusEnum = RequestStatusEnum;

  @Prop({ required: true })
  value!: IPopulatedSnapshotSignRequest;

  @Prop({ required: true })
  token!: string;

  @Ref("signaturePad")
  signaturePad!: VueSignaturePad | undefined;

  signature: MrfiktivCreateSignDtoGen = {
    firstName: "",
    lastName: "",
    location: ""
  };

  loading = false;
  loadingReject = false;
  loadingDownload = false;
  isSignSheetOpen = false;

  signPadMotivationalKey = 0;

  snapshotDetailResponseKey = 0;

  isValid = true;
  terms = false;
  isSigned = false;

  clientHeight = document.documentElement.clientHeight;
  clientWidth = document.documentElement.clientWidth;

  get hasSignature() {
    if (!this.signaturePad) return false;
    const { isEmpty } = this.signaturePad.saveSignature();
    return !isEmpty;
  }

  get overlayStyle() {
    if (this.$vuetify.theme.dark) {
      return "background-color:#1E1E1E;";
    } else {
      return "color: rgba(0, 0, 0, 0.6);";
    }
  }

  get pdfUrl() {
    const doc = this.signDocument;
    const url = doc?.url;
    return url ? `${url}` : undefined;
  }

  get signDocument(): MrfiktivDocumentViewModelGen | undefined {
    const docs = (this.value && this.value.snapshot && this.value.snapshot.documents) || [];
    return docs.length ? docs[docs.length - 1] : undefined;
  }

  get widthSignature() {
    if (this.isXsBreakpointOrDown) {
      return this.clientWidth - 45;
    }

    if (this.isBreakpointOrDown) {
      return 150 * 2.275;
    }

    return 200 * 2.275;
  }

  get heightSignature() {
    return `${this.widthSignature / 2.275}px`;
  }

  get widthStyle() {
    return `width: ${this.widthSignature}px`;
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get isXsBreakpointOrDown() {
    return this.clientWidth < 412;
  }

  get isBreakpointOrDown() {
    return this.clientWidth < 1048;
  }

  get subtitle() {
    return [RequestStatusEnum.DRAFT, RequestStatusEnum.REQUESTED].includes(this.value.status)
      ? $t("snapshot.signRequest.sign.requestDetail.subtitle.initial")
      : $t("snapshot.signRequest.sign.requestDetail.subtitle.done");
  }

  get chips() {
    return [RequestStatusEnum.DRAFT, RequestStatusEnum.REQUESTED].includes(this.value.status)
      ? []
      : [
          {
            text: $t(`enums.RequestStatusEnum.${this.value.status}`),
            key: "statsu",
            color: SnapshotSignRequest.statusColorMap.get(this.value.status)
          }
        ];
  }
  openSignSheet() {
    this.isSignSheetOpen = true;
    this.$nextTick(() => {
      this.signPadMotivationalKey++;
    });
  }

  closeSignSheet() {
    this.isSignSheetOpen = false;
  }

  clear() {
    this.isSigned = false;
    this.signaturePad?.clearSignature?.();
  }

  onBegin() {
    this.isSigned = true;
  }

  get locationText() {
    if (this.signature.location) {
      return `${this.signature.location}, ${simpleDoubleDigitDate(new Date().toISOString())}`;
    }

    return simpleDoubleDigitDate(new Date().toISOString());
  }

  async confirmSign() {
    try {
      this.loading = true;

      if (!this.isValid) {
        this.$toast.error(this.$t("snapshot.signRequest.sign.signatureRequired"));
        return;
      }

      if (!this.signaturePad) {
        this.$log.error("Signature pad is not defined");
        return;
      }

      const { isEmpty, data } = this.signaturePad.saveSignature();
      if (isEmpty || !data) {
        this.$toast.error(this.$t("snapshot.signRequest.sign.signatureRequired"));
        return;
      }

      const fileName = createFileName(this.signature.lastName, FileType.PNG);
      const blob = dataURItoBlob(data);
      const image = new File([blob], fileName, { type: blob.type });

      const dto: MrfiktivCreateSignDtoGen = {
        firstName: this.signature.firstName,
        lastName: this.signature.lastName,
        location: this.signature.location || undefined,
        image
      };

      await this.value.respondAccept(dto, this.token);

      this.$toast.success(this.$t("snapshot.signRequest.sign.success"));
      this.closeSignSheet();
    } catch (e) {
      handleError(e);
    } finally {
      this.snapshotDetailResponseKey++;
      this.loading = false;
    }
  }

  async reject() {
    try {
      this.loadingReject = true;
      await snapshotSignResponseService.respondReject(this.value.partnerId, this.value.id, this.token);
      this.$toast.success(this.$t("snapshot.signRequest.sign.rejected"));
    } catch (e) {
      handleError(e);
    } finally {
      this.loadingReject = false;
    }
  }

  async downloadPdf() {
    this.loadingDownload = true;
    const first = this.signDocument;
    const pdfUrl = first?.url;
    const fileName = first?.name;
    if (!pdfUrl) {
      this.loadingDownload = false;
      return;
    }
    await downloadUrlFromRemoteOrigin(pdfUrl, fileName || "document.pdf").catch(handleError);
    this.loadingDownload = false;
  }

  mounted() {
    // keep client width updated in case component is rendered in different viewport sizes
    window.addEventListener("resize", this.onResize.bind(this));
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize.bind(this));
  }

  onResize() {
    this.clientHeight = document.documentElement.clientHeight;
    this.clientWidth = document.documentElement.clientWidth;
  }
}
